import { useState } from 'react'
import { Link } from 'react-router-dom'
import { buildEditFormInitialValues } from './helpers/buildEditFormInitialValues'
import { buildEditSubmitBody } from './helpers/buildEditSubmitBody'
import { isEditFormValid } from './helpers/isEditFormValid'
import { validateCron } from '../../helpers/validateCron'
import { SearchableSelect } from '../SearchableSelect/SearchableSelect'
import type { EditEntityFormProps } from './EditEntityForm.types'
import '../CreateEntityForm/CreateEntityForm.css'
import '../DetailEntityFields/DetailEntityFields.css'
import { sortSelectOptions } from '../../helpers/sortSelectOptions'

export function EditEntityForm({
  rows,
  onSubmit,
  submitLabel = 'Save',
  isLoading = false,
  cancelHref,
}: EditEntityFormProps) {
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    buildEditFormInitialValues(rows)
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [touched, setTouched] = useState<Set<string>>(new Set())

  const formIsValid = isEditFormValid(rows, values)

  function setValue(name: string, value: unknown) {
    const field = rows
      .flatMap((r) => (r.mode === 'editable' ? [r] : []))
      .find((r) => r.field.name === name)?.field
    const derived = field?.deriveValues?.(value) ?? {}
    setValues((prev) => ({ ...prev, [name]: value, ...derived }))
  }

  function markTouched(name: string) {
    setTouched((prev) => new Set(prev).add(name))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!formIsValid || isSubmitting || isLoading) return

    setIsSubmitting(true)
    try {
      await onSubmit(buildEditSubmitBody(rows, values))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={isLoading}>
        <div className="create-form-table">
          {rows.map((row, index) => {
            const isLast = index === rows.length - 1
            const rowClass = isLast
              ? 'create-form-row--last'
              : 'create-form-row'

            if (row.mode === 'readonly') {
              return (
                <div key={row.label} className={rowClass}>
                  <div className="create-form-label">{row.label}</div>
                  <div className="detail-form-value-cell">{row.value}</div>
                </div>
              )
            }

            const { field } = row
            const cronError =
              field.type === 'cron' && touched.has(field.name)
                ? validateCron(String(values[field.name] ?? ''))
                : null
            const customError =
              touched.has(field.name) && field.validate
                ? field.validate(values[field.name])
                : null
            const validationError = cronError ?? customError

            return (
              <div key={field.name} className={rowClass}>
                <div className="create-form-label">
                  {field.label}
                  {field.type !== 'checkbox' && field.required !== false && (
                    <span className="create-form-required" aria-hidden="true">
                      *
                    </span>
                  )}
                </div>
                <div className="create-form-input-cell">
                  {field.type === 'text' && (
                    <div className="w-100">
                      <input
                        type="text"
                        className={`form-control form-control-sm${validationError ? ' is-invalid' : ''}`}
                        value={String(values[field.name] ?? '')}
                        onChange={(e) => setValue(field.name, e.target.value)}
                        onBlur={() => markTouched(field.name)}
                        required={field.required !== false}
                        disabled={field.isDisabled}
                        aria-label={field.label}
                      />
                      {validationError && (
                        <div className="invalid-feedback d-block">
                          {validationError}
                        </div>
                      )}
                    </div>
                  )}
                  {field.type === 'cron' && (
                    <div className="w-100">
                      <input
                        type="text"
                        className={`form-control form-control-sm font-monospace${validationError ? ' is-invalid' : ''}`}
                        value={String(values[field.name] ?? '')}
                        placeholder="* * * * *"
                        onChange={(e) => setValue(field.name, e.target.value)}
                        onBlur={() => markTouched(field.name)}
                        required={field.required !== false}
                      />
                      {validationError && (
                        <div className="invalid-feedback d-block">
                          {validationError}
                        </div>
                      )}
                    </div>
                  )}
                  {field.type === 'number' && (
                    <input
                      type="number"
                      step="any"
                      className="form-control form-control-sm"
                      value={String(values[field.name] ?? '')}
                      onChange={(e) => setValue(field.name, e.target.value)}
                      onBlur={() => markTouched(field.name)}
                      required={field.required !== false}
                    />
                  )}
                  {field.type === 'checkbox' && (
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={field.name}
                      checked={Boolean(values[field.name])}
                      onChange={(e) => setValue(field.name, e.target.checked)}
                    />
                  )}
                  {field.type === 'select' && field.searchable && (
                    <SearchableSelect
                      value={String(values[field.name] ?? '')}
                      options={field.options}
                      ariaLabel={field.label}
                      isRequired={field.required !== false}
                      onChange={(value) => setValue(field.name, value)}
                      onBlur={() => markTouched(field.name)}
                    />
                  )}
                  {field.type === 'select' && !field.searchable && (
                    <select
                      className="form-select form-select-sm"
                      value={String(values[field.name] ?? '')}
                      onChange={(e) => setValue(field.name, e.target.value)}
                      onBlur={() => markTouched(field.name)}
                      required={field.required !== false}
                    >
                      <option value="">Select…</option>
                      {sortSelectOptions(field.options).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </fieldset>

      <div className="d-flex justify-content-end gap-2">
        {cancelHref && (
          <Link to={cancelHref} className="btn btn-outline-secondary btn-sm">
            Cancel
          </Link>
        )}
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={!formIsValid || isSubmitting || isLoading}
        >
          {isSubmitting && (
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            />
          )}
          {submitLabel}
        </button>
      </div>
    </form>
  )
}
